import React, { useEffect, useState } from 'react';
import { HiSpeakerphone, HiCheckCircle, HiEye, HiCursorClick } from '../components/Icons';
import { getAllPromotions, getAllPromotionStats, getCategories } from '../services/api';
import { Promotion, PromotionCategory, ImpressionStats } from '../services/api';
import './AdminDashboard.css';

const PROMO_TYPE_LABELS: Record<string, string> = { story: 'Story', spotlight: 'Spotlight', category: 'Category' };

const AdminDashboard: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [categories, setCategories] = useState<PromotionCategory[]>([]);
  const [stats, setStats] = useState<ImpressionStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [promotionsRes, statsRes, categoriesRes] = await Promise.all([
        getAllPromotions(),
        getAllPromotionStats(),
        getCategories(),
      ]);
      setPromotions(promotionsRes.data);
      setStats(statsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (promo: Promotion): string => {
    if (promo.category_id != null) {
      const cat = categories.find((c) => c.id === promo.category_id);
      if (cat) return cat.name;
    }
    return PROMO_TYPE_LABELS[promo.promotion_type] ?? promo.promotion_type;
  };

  const totalPromotions = promotions.length;
  const activePromotions = promotions.filter(p => p.is_active === 1).length;
  const totalImpressions = stats.reduce((sum, s) => sum + s.total_impressions, 0);
  const totalClicks = stats.reduce((sum, s) => sum + s.clicks, 0);

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><HiSpeakerphone size={28} /></div>
          <div className="stat-content">
            <h3>Total Promotions</h3>
            <p className="stat-value">{totalPromotions}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><HiCheckCircle size={28} /></div>
          <div className="stat-content">
            <h3>Active Promotions</h3>
            <p className="stat-value">{activePromotions}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><HiEye size={28} /></div>
          <div className="stat-content">
            <h3>Total Impressions</h3>
            <p className="stat-value">{totalImpressions.toLocaleString()}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><HiCursorClick size={28} /></div>
          <div className="stat-content">
            <h3>Total Clicks</h3>
            <p className="stat-value">{totalClicks.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="recent-promotions">
        <h2>Recent Promotions</h2>
        <div className="promotions-grid">
          {promotions.slice(0, 6).map((promo) => (
            <div key={promo.id} className="promo-card">
              <h4 className="promo-title">{promo.title}</h4>
              <p className="promo-category">Category {getCategoryLabel(promo)}</p>
              <span className={`status-badge ${promo.is_active === 1 ? 'active' : 'inactive'}`}>
                {promo.is_active === 1 ? 'Active' : 'Inactive'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

